import React, { useRef, useState } from "react";
import { useScroll, useMotionValueEvent } from "framer-motion";
import Reveal from "../site/Reveal";
import { SectionLabel } from "../site/Bits";
import { APPROACH } from "../../lib/content";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

export default function Approach() {
  const reduced = usePrefersReducedMotion();
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start 78%", "end 45%"],
  });

  useMotionValueEvent(scrollYProgress, "change", (value) => {
    const next = Math.max(0, Math.min(APPROACH.length - 1, Math.floor(value * APPROACH.length)));
    setActive(next);
  });

  const progress = reduced ? 1 : (active + 0.5) / APPROACH.length;

  return (
    <section className="lk-section" style={{ background: "var(--lykit-lavender)" }} data-testid="approach-section">
      <div className="lk-container-wide">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <SectionLabel>Our Approach</SectionLabel>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="display-lg mt-7">FROM IDEA TO IMPACT.</h2>
            </Reveal>
          </div>
          <Reveal delay={0.14}>
            <p className="max-w-[34ch] text-[14px] leading-[1.6] body-muted">
              Five stages that carry a project from the first conversation to measurable outcome.
            </p>
          </Reveal>
        </div>

        <div ref={trackRef} className="relative mt-[clamp(30px,3.2vw,50px)]">
          {/* desktop track */}
          <div className="hidden lg:block">
            <div className="relative h-[1px] w-full" style={{ background: "rgba(23,21,34,0.14)" }}>
              <span
                aria-hidden="true"
                className="absolute top-0 left-0 h-[1px]"
                style={{
                  width: `${progress * 100}%`,
                  background: "rgba(216,183,122,0.9)",
                  transition: "width 700ms var(--ease-out)",
                }}
              />
              <span
                aria-hidden="true"
                className="absolute h-[7px] w-[7px] rounded-full"
                style={{
                  left: `calc(${progress * 100}% - 3.5px)`,
                  top: -3,
                  background: "#D8B77A",
                  transition: "left 700ms var(--ease-out)",
                }}
              />
            </div>

            <ol className="grid grid-cols-5 gap-8 pt-10" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {APPROACH.map((step, i) => {
                const isActive = reduced || i <= active;
                return (
                  <li
                    key={step.num}
                    data-testid={`approach-step-${step.num}`}
                    style={{
                      opacity: isActive ? 1 : 0.4,
                      transition: "opacity 600ms var(--ease-out)",
                    }}
                  >
                    <span
                      className="block font-display text-[13px] font-bold"
                      style={{ letterSpacing: "0.14em", color: i === active && !reduced ? "#B08A4B" : "rgba(23,21,34,0.42)" }}
                    >
                      {step.num}
                    </span>
                    <h3 className="mt-4 font-display text-[clamp(17px,1.5vw,22px)] font-extrabold tracking-[-0.02em]">
                      {step.title}
                    </h3>
                    <p className="mt-3 text-[14px] leading-[1.6] body-muted">{step.text}</p>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* mobile / tablet list */}
          <ol className="lg:hidden" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {APPROACH.map((step) => (
              <li
                key={step.num}
                data-testid={`approach-step-mobile-${step.num}`}
                className="flex gap-6 py-7"
                style={{ borderTop: "1px solid rgba(23,21,34,0.12)" }}
              >
                <span
                  className="shrink-0 font-display text-[13px] font-bold"
                  style={{ letterSpacing: "0.14em", color: "#B08A4B", paddingTop: 4 }}
                >
                  {step.num}
                </span>
                <div>
                  <h3 className="font-display text-[19px] font-extrabold tracking-[-0.02em]">{step.title}</h3>
                  <p className="mt-2 text-[14px] leading-[1.6] body-muted">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
